/* Rules Match board — turn-based lane combat under the seven core keywords.
   Pick a card from your hand, post it to a lane, bank scrap for Overclock,
   and burn the NET Commander down from 30. Quick Skirmish stays untouched. */
import { AnimatePresence, motion } from "framer-motion";
import { BookOpenText, Lock, RotateCcw, Swords, Wrench, Zap } from "lucide-react";
import { useRef, useState } from "react";
import { getCardRules, isPveOnly } from "../data/tcgRules";
import { awardFeat, awardPoints } from "../utils/rewards";
import { cardPower, postBattleRecord } from "../utils/tcg";
import { COUNCIL_PERSONAS, effAtk, endRound, hasKw, isTerrainCard, jamUnit, matchCost, MATCH_ROUNDS, newMatch, playCard, riotTrigger, terrainRule } from "../utils/tcgMatch";

const LADDER_KEY = "wr-council-ladder";
const readLadder = () => {
  try {
    return Math.min(COUNCIL_PERSONAS.length - 1, Math.max(0, Number(localStorage.getItem(LADDER_KEY)) || 0));
  } catch {
    return 0;
  }
};

const shortName = (card) => card.name.split(",")[0].split("—")[0].trim();

const UnitCard = ({ unit, side, lane, jamMode, onJamStart, onLaneClick, match }) => {
  const rules = getCardRules(unit.card);
  const atk = effAtk(match[side], unit);
  const canJam = side === "you" && !unit.jammed && hasKw(unit, "Jam") && !match.over;
  return (
    <div
      role="button"
      tabIndex={0}
      className={`rules-unit rarity-${unit.card.rarity}${unit.jammed ? " is-jammed" : ""}${jamMode && side === "net" ? " is-jam-target" : ""}`}
      onClick={() => onLaneClick(side, lane)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onLaneClick(side, lane);
        }
      }}
      data-testid={`rules-unit-${side}-${lane}`}
    >
      <img src={unit.card.image} alt={`${unit.card.name} in play`} />
      <span className="rules-unit-name">{shortName(unit.card)}</span>
      <span className="rules-unit-stats">
        <b>{atk} ATK</b> · <b>{unit.hp} HP</b>
      </span>
      <span className="rules-unit-kws">
        {unit.unstable && <em className="kw-unstable">UNSTABLE 3</em>}
        {rules.keywords.map((k) => (
          <em key={k} className={unit.jammed ? "kw-dead" : ""}>{k.toUpperCase()}</em>
        ))}
      </span>
      {unit.jammed && <span className="rules-jammed-tag">JAMMED</span>}
      {canJam && (
        <button
          type="button"
          className="rules-jam-button"
          onClick={(e) => {
            e.stopPropagation();
            onJamStart(lane);
          }}
          data-testid={`rules-jam-${lane}`}
        >
          <Zap size={11} /> JAM
        </button>
      )}
    </div>
  );
};

export function TcgRulesMatch({ deckIds, user, onRebuild }) {
  const matchRef = useRef(null);
  const [, setTick] = useState(0);
  const [selected, setSelected] = useState(null); // hand index
  const [jamLane, setJamLane] = useState(null); // your lane whose Jam is armed
  const [hint, setHint] = useState("");
  const [busy, setBusy] = useState(false);
  const [rewarded, setRewarded] = useState(false);
  const [advancedSeat, setAdvancedSeat] = useState(null); // persona unlocked by this win
  const [unlocked, setUnlocked] = useState(readLadder); // council ladder progress
  const [personaId, setPersonaId] = useState(() => COUNCIL_PERSONAS[readLadder()].id);
  const commit = () => setTick((t) => t + 1);
  const match = matchRef.current;

  const start = () => {
    matchRef.current = newMatch(deckIds, personaId);
    setSelected(null);
    setJamLane(null);
    setHint("");
    setRewarded(false);
    setAdvancedSeat(null);
    commit();
  };

  if (!match) {
    const deck = deckIds.map((id) => id).filter(Boolean);
    return (
      <div className="battle-ready" data-testid="rules-ready">
        <div className="battle-ready-copy">
          <h3>THE PROPER WAR. THIRTY HIT POINTS.</h3>
          <p>
            Your six-card deck is your whole hand. Commanders start at 30 HP across three lanes. Scrap opens at
            3 and grows each round — bank it and your Overclock units fire for free. Guard walls intercept,
            Corrupted bombs bill their own side, Jam kills enemy abilities AND terrain for a round, and Bounty
            pays on every kill. Enemy riot lanes can be bribed into flipping on their own unit for 2 scrap.
            Climb all five council seats — each one out-earns the last.
          </p>
          <div className="council-ladder" data-testid="council-ladder">
            {COUNCIL_PERSONAS.map((p, i) => {
              const lockedSeat = i > unlocked;
              return (
                <button
                  type="button"
                  key={p.id}
                  className={`council-seat${personaId === p.id ? " is-active" : ""}${lockedSeat ? " is-locked" : ""}`}
                  onClick={() => !lockedSeat && setPersonaId(p.id)}
                  disabled={lockedSeat}
                  data-testid={`council-seat-${p.id}`}
                  aria-label={lockedSeat ? `${p.name} — locked` : `Challenge ${p.name}`}
                >
                  <strong>{lockedSeat ? <Lock size={11} /> : null} {p.name}</strong>
                  <span>{p.title}</span>
                  <small>{p.style}</small>
                  <em>+{p.income} SCRAP/RD · WIN +{p.pts} PTS</em>
                </button>
              );
            })}
          </div>
          <div className="battle-start-row">
            <button type="button" className="battle-start-button" onClick={start} data-testid="rules-start-button" disabled={!deck.length}>
              <BookOpenText size={16} /> CHALLENGE {COUNCIL_PERSONAS.find((p) => p.id === personaId)?.name}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const playInto = (lane) => {
    if (selected === null || busy || match.over) return;
    const res = playCard(match, "you", selected, lane);
    if (!res.ok) {
      setHint(res.reason);
    } else {
      setHint("");
      setSelected(null);
    }
    commit();
  };

  const clickLane = (side, lane) => {
    if (busy || match.over) return;
    if (jamLane !== null) {
      if (side === "net" && match.net.lanes[lane]) {
        jamUnit(match, "you", jamLane, lane);
        setJamLane(null);
        commit();
      }
      return;
    }
    if (side === "you") playInto(lane);
  };

  const resolveRound = () => {
    if (busy || match.over) return;
    setBusy(true);
    setSelected(null);
    setJamLane(null);
    setHint("THE COUNCIL MOVES…");
    setTimeout(() => {
      endRound(match);
      setBusy(false);
      setHint("");
      if (match.over && !rewarded) {
        setRewarded(true);
        if (match.winner === "you") {
          awardPoints("tcg", match.persona.pts);
          awardFeat("RULESKEEPER");
          const seatIndex = COUNCIL_PERSONAS.findIndex((p) => p.id === match.persona.id);
          if (seatIndex === unlocked && unlocked < COUNCIL_PERSONAS.length - 1) {
            const next = unlocked + 1;
            setUnlocked(next);
            setAdvancedSeat(COUNCIL_PERSONAS[next].name);
            try {
              localStorage.setItem(LADDER_KEY, String(next));
            } catch {}
          }
        } else if (match.winner === "draw") {
          awardPoints("tcg", 4);
        }
        if (user) postBattleRecord(match.winner === "draw" ? "draw" : match.winner === "you" ? "win" : "loss", 0).catch(() => {});
      }
      commit();
    }, 1100);
  };

  /* terrain chips double as counter-play surfaces: jam silences them for the
     round, and enemy riot lanes can be bribed into flipping on their own unit */
  const TerrainTag = ({ side, lane }) => {
    const s = match[side];
    const t = s.terrain[lane];
    if (!t) return null;
    const rule = terrainRule(t);
    const jammed = s.terrainJammed[lane];
    const jamTarget = side === "net" && jamLane !== null && !jammed;
    const bribeable = side === "net" && rule === "riot" && match.net.lanes[lane] && !jammed && !match.over && !busy && jamLane === null;
    return (
      <span
        className={`rules-terrain-tag${jammed ? " is-jammed" : ""}${jamTarget ? " is-jam-target" : ""}`}
        role={jamTarget ? "button" : undefined}
        onClick={
          jamTarget
            ? (e) => {
                e.stopPropagation();
                jamUnit(match, "you", jamLane, lane, "terrain");
                setJamLane(null);
                commit();
              }
            : undefined
        }
        data-testid={`rules-terrain-${side}-${lane}`}
      >
        {shortName(t)}{jammed ? " — JAMMED" : ""}
        {bribeable && (
          <button
            type="button"
            className="rules-bribe-button"
            onClick={(e) => {
              e.stopPropagation();
              const res = riotTrigger(match, "you", lane);
              if (!res.ok) setHint(res.reason);
              commit();
            }}
            data-testid={`rules-bribe-${lane}`}
          >
            BRIBE 2 SCRAP
          </button>
        )}
      </span>
    );
  };

  const laneZone = (side) => (
    <div className={`rules-lanes rules-lanes-${side}`} data-testid={`rules-lanes-${side}`}>
      {[0, 1, 2].map((lane) => {
        const s = match[side];
        const unit = s.lanes[lane];
        const dropReady = side === "you" && selected !== null && !match.over;
        return (
          <div key={lane} className="rules-lane-stack">
            {side === "you" && <TerrainTag side={side} lane={lane} />}
            <div
              role="button"
              tabIndex={0}
              className={`rules-lane${unit ? " is-filled" : ""}${dropReady && !unit ? " is-droppable" : ""}${jamLane !== null && side === "net" && unit ? " is-jam-target" : ""}`}
              onClick={() => clickLane(side, lane)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  clickLane(side, lane);
                }
              }}
              data-testid={`rules-lane-${side}-${lane}`}
              aria-label={`Lane ${lane + 1} ${side}`}
            >
              {unit ? (
                <UnitCard unit={unit} side={side} lane={lane} jamMode={jamLane !== null} onJamStart={(l) => setJamLane(l === jamLane ? null : l)} onLaneClick={clickLane} match={match} />
              ) : (
                <span className="rules-lane-empty">{side === "you" && selected !== null ? "POST HERE" : `LANE ${lane + 1}`}</span>
              )}
            </div>
            {side === "net" && <TerrainTag side={side} lane={lane} />}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="rules-arena" data-testid="rules-arena">
      <div className="battle-scoreboard rules-scoreboard" data-testid="rules-scoreboard">
        <span className="score-you" data-testid="rules-hp-you">YOU {match.you.hp} HP · {match.you.scrap} SCRAP</span>
        <span className="score-round" data-testid="rules-round">{match.over ? "FINAL" : `VS ${match.persona.name} — ROUND ${match.round} / ${MATCH_ROUNDS}`}</span>
        <span className="score-net" data-testid="rules-hp-net">NET {match.net.hp} HP · {match.net.scrap} SCRAP</span>
      </div>

      {laneZone("net")}

      <div className="rules-midband">
        <button
          type="button"
          className="battle-start-button rules-end-button"
          onClick={resolveRound}
          disabled={busy || match.over}
          data-testid="rules-end-round-button"
        >
          <Swords size={15} /> {busy ? "RESOLVING…" : "END ROUND — CLASH"}
        </button>
        <div className="rules-log" data-testid="rules-log">
          {(hint ? [hint, ...match.log] : match.log).slice(0, 5).map((entry, i) => (
            <p key={`${i}-${entry}`} className={i === 0 && hint ? "rules-hint-line" : ""}>{entry}</p>
          ))}
        </div>
      </div>

      {jamLane !== null && (
        <p className="rules-jam-banner" data-testid="rules-jam-banner">JAM ARMED — pick an enemy unit to silence. Tap the same unit to cancel.</p>
      )}

      {laneZone("you")}

      {!match.over && (
        <div className="battle-hand rules-hand" data-testid="rules-hand">
          {match.you.hand.map((card, i) => {
            const cost = matchCost(card);
            const terrain = isTerrainCard(card);
            const afford = cost <= match.you.scrap;
            return (
              <button
                type="button"
                key={card.id}
                className={`battle-hand-card rules-hand-card rarity-${card.rarity}${selected === i ? " is-selected" : ""}${!afford ? " is-locked" : ""}`}
                onClick={() => setSelected(selected === i ? null : i)}
                data-testid={`rules-hand-card-${card.id}`}
                aria-label={`Select ${card.name}, costs ${cost} scrap`}
              >
                <img src={card.image} alt={`${card.name} card`} />
                <span>{shortName(card)}</span>
                <small>{terrain ? "TERRAIN" : `PWR ${cardPower(card)}`} · {cost} SCRAP</small>
                {isPveOnly(card) && <em className="hand-ability">PvE ONLY</em>}
              </button>
            );
          })}
          {match.you.hand.length === 0 && <p className="rules-hand-empty">HAND SPENT — YOUR LANES FIGHT ON THEIR OWN NOW.</p>}
        </div>
      )}

      <AnimatePresence>
        {match.over && (
          <motion.div
            className={`battle-result ${match.winner === "you" ? "victory" : match.winner === "draw" ? "draw" : "defeat"}`}
            data-testid="rules-result"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <h3 data-testid="rules-result-title">
              {match.winner === "you" ? "COMMANDER DOWN. THE CITY IS YOURS." : match.winner === "draw" ? "BOTH COMMANDERS LIMP HOME." : "YOUR COMMANDER FLATLINES."}
            </h3>
            <p className="battle-result-score">YOU {Math.max(0, match.you.hp)} — NET {Math.max(0, match.net.hp)} · {match.round} ROUNDS</p>
            <p className="battle-result-points" data-testid="rules-points-earned">
              {match.winner === "you" ? `+${match.persona.pts} PTS BANKED — FEAT: RULESKEEPER` : match.winner === "draw" ? "+4 PTS FOR THE STALEMATE" : "NO PTS — REBUILD AND RUN IT BACK."}
            </p>
            {advancedSeat && (
              <p className="battle-boss-claim" data-testid="rules-unlock-note">
                NEXT SEAT UNLOCKED — {advancedSeat} AWAITS.
              </p>
            )}
            <div className="battle-actions">
              <button type="button" onClick={start} data-testid="rules-rematch-button">
                <RotateCcw size={15} /> RUN IT BACK
              </button>
              <button type="button" className="is-secondary" onClick={onRebuild} data-testid="rules-rebuild-button">
                <Wrench size={15} /> REBUILD DECK
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
